import React from 'react';
import { CreditCard, RefreshDouble, WarningCircle } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { usePaymentHistory, downloadInvoice, type PaymentHistoryItem } from '../../features/settings/hooks';
import { useNavigate } from 'react-router-dom';

function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation('settings');
  if (status === 'succeeded') {
    return (
      <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
        {t('payments.status.paid')}
      </span>
    );
  }
  if (status === 'failed' || status === 'canceled') {
    return (
      <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-700">
        {t('payments.status.declined')}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700">
      {t('payments.status.pending')}
    </span>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd} ${hh}:${min}`;
}

function formatAmount(cents: number, currency: string): string {
  const symbol = currency.toLowerCase() === 'eur' ? '€' : currency.toUpperCase();
  return `${symbol}${(cents / 100).toFixed(2)}`;
}

function PaymentRow({ payment }: { payment: PaymentHistoryItem }) {
  const { t } = useTranslation(['settings', 'common']);
  const navigate = useNavigate();
  const [invoiceLoading, setInvoiceLoading] = React.useState(false);

  const handleDownloadInvoice = async () => {
    setInvoiceLoading(true);
    try {
      await downloadInvoice(payment.payment_uuid);
    } finally {
      setInvoiceLoading(false);
    }
  };

  return (
    <tr className="border-b border-[#e2e8f0] last:border-0">
      <td className="px-4 py-3 text-sm text-gray-900 border-r border-[#e2e8f0]">
        {formatAmount(payment.amount_cents, payment.currency)}
      </td>
      <td className="px-4 py-3 text-sm text-gray-600 border-r border-[#e2e8f0]">
        {formatDate(payment.created_at)}
      </td>
      <td className="px-4 py-3 border-r border-[#e2e8f0]">
        <StatusBadge status={payment.status} />
      </td>
      <td className="px-4 py-3 text-sm">
        {payment.status === 'succeeded' ? (
          <button
            type="button"
            disabled={invoiceLoading}
            className="text-blue-600 hover:underline disabled:opacity-50"
            onClick={handleDownloadInvoice}
          >
            {invoiceLoading ? t('common:loading.generic') : t('settings:payments.download_invoice')}
          </button>
        ) : payment.status === 'failed' || payment.status === 'canceled' ? (
          <button
            type="button"
            className="text-sm text-blue-600 hover:underline"
            onClick={() => navigate('/new')}
          >
            {t('settings:payments.retry_payment')}
          </button>
        ) : null}
      </td>
    </tr>
  );
}

export default function PaymentHistoryTab() {
  const { t } = useTranslation(['settings', 'common']);
  const { data, isLoading, isError, refetch } = usePaymentHistory();

  if (isLoading) {
    return <p className="text-sm text-gray-500 py-4">{t('common:loading.generic')}</p>;
  }
  if (isError) {
    return (
      <div className="flex flex-col items-center gap-3 py-12 text-center">
        <WarningCircle className="h-8 w-8 text-gray-400" />
        <p className="text-sm font-medium text-gray-700">{t('settings:payments.load_error')}</p>
        <p className="text-xs text-gray-500">{t('settings:shared.load_error_hint')}</p>
        <button
          type="button"
          onClick={() => refetch()}
          className="mt-1 inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <RefreshDouble className="h-4 w-4" />
          {t('common:buttons.try_again')}
        </button>
      </div>
    );
  }
  if (!data || data.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 py-16 text-center">
        <div className="rounded-full bg-gray-100 p-4">
          <CreditCard className="h-7 w-7 text-gray-400" />
        </div>
        <p className="text-sm font-medium text-gray-700">{t('settings:payments.empty_title')}</p>
        <p className="text-xs text-gray-500 max-w-xs">{t('settings:payments.empty_body')}</p>
      </div>
    );
  }

  return (
    <div className="border border-[#e2e8f0] rounded-lg overflow-hidden">
      <table className="w-full text-left">
        <thead>
          <tr className="border-b border-[#e2e8f0] bg-[#f9fafb]">
            <th className="px-4 py-3 text-xs font-semibold text-gray-500 border-r border-[#e2e8f0]">
              {t('settings:payments.table.amount')}
            </th>
            <th className="px-4 py-3 text-xs font-semibold text-gray-500 border-r border-[#e2e8f0]">
              {t('settings:payments.table.date')}
            </th>
            <th className="px-4 py-3 text-xs font-semibold text-gray-500 border-r border-[#e2e8f0]">
              {t('settings:payments.table.type')}
            </th>
            <th className="px-4 py-3 text-xs font-semibold text-gray-500"></th>
          </tr>
        </thead>
        <tbody>
          {data.map((p) => (
            <PaymentRow key={p.payment_uuid} payment={p} />
          ))}
        </tbody>
      </table>
    </div>
  );
}
